import { createRouteArcPath, type LngLat, type RouteArcPath } from "./route-arc";

export interface MapRoute {
  origin: LngLat;
  current: LngLat | null;
  destination: LngLat;
  completed: RouteArcPath | null;
  remaining: RouteArcPath | null;
}

export interface RouteLeg<Route extends MapRoute> {
  route: Route;
  arc: RouteArcPath;
}

export interface MapRouteData<Route extends MapRoute> {
  routes: Route[];
  completedLegs: RouteLeg<Route>[];
  remainingLegs: RouteLeg<Route>[];
  currentRoutes: Route[];
}

export function createMapRouteData<Route extends MapRoute>(routes: Route[]): MapRouteData<Route> {
  return {
    routes,
    completedLegs: routes.flatMap((route) => (route.completed ? [{ route, arc: route.completed }] : [])),
    remainingLegs: routes.flatMap((route) => (route.remaining ? [{ route, arc: route.remaining }] : [])),
    currentRoutes: routes.filter((route) => route.current !== null),
  };
}

export function createMapRoute(origin: LngLat, destination: LngLat, current: LngLat | null): MapRoute {
  const departure = current ?? origin;

  return {
    origin,
    current,
    destination,
    completed: current && !areSameCoords(origin, current) ? createRouteArcPath(origin, current) : null,
    remaining: !areSameCoords(departure, destination) ? createRouteArcPath(departure, destination) : null,
  };
}

function areSameCoords(left: LngLat, right: LngLat): boolean {
  return left[0] === right[0] && left[1] === right[1];
}
